import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip, IconButton, InputLabel, Tooltip, TextField, Autocomplete } from '@mui/material';
import { Help as HelpIcon, OpenInNew as OpenInNewIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { generatePath, Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import useActiveSite from '../../../../../hooks/useActiveSite';
import { fetchSiteGroups } from '../../../../../actions/groups';
import { Colors } from '../../../../../assets/theme/Colors';
import routes from '../../../../../routes';
import NewLabel from '../../../../../components/NewLabel';
import { FF_USER_GROUPS } from '../../../../../components/FeatureFlagWrapper';

const GroupsSelector = ({ disabled, selectedGroups = [], onChange }) => {
  const dispatch = useDispatch();
  const activeSite = useActiveSite();

  const groups = useSelector(state => state.entities.groups);
  const siteGroupIds = groups.bySiteId[activeSite.id] || [];
  const siteGroups = siteGroupIds.map(id => groups.byId[id]).filter(Boolean);

  const selectedIds = new Set(selectedGroups.map(g => g.id));
  const value = siteGroups.filter(g => selectedIds.has(g.id));

  const handleChange = (_event, newValue) => {
    onChange(newValue);
  };

  return (
    <Box>
      <Box display="flex" alignItems="baseline" justifyContent="space-between">
        <InputLabel shrink style={{ marginBottom: 4 }}>
          Visible to Groups&nbsp;
          <NewLabel featureFlag={FF_USER_GROUPS} />
        </InputLabel>
        {/* Hide link on native devices */}
        {!Capacitor.isNativePlatform() && (
          <Link
            to={generatePath(routes.allUsers, { tab: 'groups' })}
            target="_blank"
            rel="noopener noreferrer"
            style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: Colors.gray, display: 'inline-flex', alignItems: 'center', gap: 2 }}
          >
            Manage groups <OpenInNewIcon style={{ fontSize: '12px' }} />
          </Link>
        )}
      </Box>
      <Box display="flex" alignItems="center" gap="4px">
        <Autocomplete
          style={{ flex: 1 }}
          multiple
          disabled={disabled}
          options={siteGroups}
          getOptionLabel={g => g.name}
          getOptionSelected={(option, val) => option.id === val.id}
          value={value}
          onChange={handleChange}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                key={option.id}
                label={option.name}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={value.length === 0 ? 'All rescuers can see this donation' : ''}
            />
          )}
        />

        <Tooltip title="Refresh groups" placement="top">
          <IconButton size="small" onClick={() => dispatch(fetchSiteGroups(activeSite.id))}>
            <RefreshIcon style={{ fontSize: '14px', color: Colors.gray }} />
          </IconButton>
        </Tooltip>
        <Tooltip arrow placement="top" title="When one or more groups are selected, only rescuers who belong to at least one of those groups will see this donation on their schedule.">
          <HelpIcon htmlColor={Colors.blue.main} />
        </Tooltip>
      </Box>
    </Box>
  );
};

export default GroupsSelector;
